import * as React from 'react';
import { FC, useState, useEffect, useRef } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import stylesheet, { blue, backgroundColor } from 'src/constants/stylesheet';
import * as classNames from 'classnames';
import mediaQueries from 'src/constants/responsive';
import { useApi } from 'src/@context/Api';

const useStyles = makeStyles(() =>
    createStyles({
        selectRoot: {
            position: 'relative',
            zIndex: 10,

            '&:focus': {
                backgroundColor: 'red',
            }
        },
        open: {},
        selectTitleOuter: {
            position: 'relative',
            display: 'flex',
            border: `1px solid ${blue}`,
            backgroundColor,
            padding: `4px 7px 2px 7px`,
            cursor: 'pointer'
        },
        selectTitle: {
            color: blue,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        selectIcon: {
            flex: ' 0 0 12px',
            width: 12,
            marginLeft: 'auto',
            '& svg': {
                transform: 'translateY(-1px)',
            },
        },
        selectOptions: {
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            height: 0,
            maxHeight: 250,
            overflow: 'hidden',
            backgroundColor: 'white',
            borderLeft: `1px solid ${blue}`,
            borderRight: `1px solid ${blue}`,
            borderBottom: `1px solid ${blue}`,

            '$open &': {
                // borderTop: `1px solid ${blue}`,
                padding: `4px 7px`,
                height: 'auto',
                overflow: 'auto',
            },

            '$isTag &': {
                display: 'flex',
                flexWrap: 'wrap',
            }
        },
        selectOption: {
            color: blue,
            cursor: 'pointer',

            '&:hover': {
                color: 'black',
            },
            '&$active': {
                color: 'black',
            },
        },
        active: {
        },
        filterContainer: {},
        isTag: {},

        tagsContainer: {
            margin: '0 -2px',

            [mediaQueries.AfterMobile]: {
                display: 'flex',
                flexWrap: 'wrap',
            }
        },
        tag: {
            [mediaQueries.Mobile]: {
                color: blue,
                cursor: 'pointer',

                '&:hover': {
                    color: 'black',
                },
                '&$active': {

                    color: 'black',
                },
            },
            [mediaQueries.AfterMobile]: {
                ...stylesheet.Tag,
                margin: '0 2px 4px',

                '&$active': {
                    color: 'white',
                    backgroundColor: blue
                },
            }
        },
        bold: {
            ...stylesheet.Bold
        }
    }));
export type TOption = { title: string; value: string };
export type DropDownProps = {
    title: string;
    options: TOption[];
    isTags: boolean;
    onChanged: (option?: TOption) => void;
}

type Props = DropDownProps;

const DropDown: FC<Props> = ({ title, options, onChanged, isTags }) => {
    const cls = useStyles();
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState<TOption>();
    const [tags, setTags] = useState<string[]>([]);

    const { app } = useApi();
    const config = app && app.Config;
    const translationsItems = config && config.TranslationItems;
    const defaultDropdown = translationsItems && translationsItems.find(t => t.UID === 'default_dropdown');

    const drop = useRef<any>(null);

    function handleClick(e: any) {
        if (!drop.current.contains(e.target) && open) {
            setOpen(false);
        }
    }

    React.useEffect(() => {
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    });

    useEffect(() => {
        onChanged(current);
    }, [current]);

    const onTagsChange = (tag: string) => {
        if (tags && tags.indexOf(tag) > -1) {

            const result = tags.filter((t, i) => i !== tags.indexOf(tag));

            // TODO: Why does this not trigger useEffect? Something about mutable and imutable object?
            // tags.splice(tags.indexOf(tag), 1);

            setTags(result);
        } else {
            setTags([...tags, tag])
        }
    }

    const arrowIcon =
        <svg
            version="1.1"
            id="arrow_icon"
            xmlns="http://www.w3.org/2000/svg"
            x="0px"
            y="0px"
            viewBox="0 0 35.5 17.8"
        // style="enable-background:new 0 0 35.5 17.8;" 
        >
            <polygon points="0,0 17.7,17.8 35.5,0 " />
        </svg>;

    const getTitle = () => {
        if (isTags && tags) {
            return tags.join(', ') || title;
        }

        if (!isTags && current) {
            return current.title;
        }

        return title;
    }

    return (
        <div
            className={classNames(cls.selectRoot, open && cls.open, isTags && cls.isTag)}
            // tslint:disable-next-line:jsx-no-lambda
            onClick={(e) => setOpen(!open)}
            ref={drop}
        >
            <div className={cls.selectTitleOuter}>
                <div className={classNames(cls.selectTitle, (current || tags.length > 0) && cls.bold)}>
                    {getTitle()}
                </div>
                <div className={cls.selectIcon}>
                    {arrowIcon}
                </div>

            </div>
            {!isTags && open &&
                <div className={cls.selectOptions}>
                    <div
                        key={'default-option'}
                        className={
                            classNames(cls.selectOption)}
                        // tslint:disable-next-line:jsx-no-lambda
                        onClick={() => setCurrent(undefined)}
                    >
                        {defaultDropdown && defaultDropdown.Translation || ''}
                    </div>

                    {options.map(option =>
                        <div
                            key={option.value}
                            className={
                                classNames(cls.selectOption,
                                    current && current.value === option.value && cls.active)
                            }
                            // tslint:disable-next-line:jsx-no-lambda
                            onClick={() => setCurrent(option)}
                        >
                            {option.title}
                        </div>
                    )}
                </div>}

            {isTags && open &&
                <div className={cls.selectOptions}>

                    <div className={cls.tagsContainer}>
                        <div
                            key={'default-option'}
                            className={classNames(cls.tag)}
                            // tslint:disable-next-line:jsx-no-lambda
                            onClick={() => {
                                setCurrent(undefined);
                                setTags([]);
                            }}
                        >
                            {defaultDropdown && defaultDropdown.Translation || ''}
                        </div>
                        {options.map(option =>
                            <div
                                key={option.value}
                                className={
                                    classNames(
                                        cls.tag, tags && tags.indexOf(option.title) > -1 && cls.active)}
                                // tslint:disable-next-line:jsx-no-lambda
                                onClick={() => {
                                    setCurrent(option);
                                    onTagsChange(option.title)
                                }}
                            >
                                {option.title}
                            </div>
                        )}
                    </div>
                </div>
            }
        </div>
    );
}

export default DropDown;


// WEBPACK FOOTER //
// ./src/Components/DropDown.tsx